import {
	requestApiDelete,
	requestApiGet,
	requestApiGetFile,
	requestApiPost,
} from "../api/BacklogApiEndpoint";
import type { BacklogConfig } from "../contracts/BacklogConfig";
import type { BacklogApiContext } from "../contracts/api/BacklogApiContext";
import type { BacklogResponse } from "../contracts/api/BacklogResponse";
import type { BacklogResponseFile } from "../contracts/api/BacklogResponseFile";
import type { Team } from "../entities/Team";
import { BacklogError } from "../errors/BacklogError";
import type { EmptyObject } from "../types/EmptyObject";
import { assert } from "../utils/BacklogUtil";
import type { AddProjectTeamParams } from "./params/AddProjectTeamParams";
import type { DeleteProjectTeamParams } from "./params/DeleteProjectTeamParams";
import type { GetListOfTeamsParams } from "./params/GetListOfTeamsParams";
import type { GetProjectTeamListParams } from "./params/GetProjectTeamListParams";
import type { GetTeamIconParams } from "./params/GetTeamIconParams";
import type { GetTeamParams } from "./params/GetTeamParams";

/**
 * Get List of Teams チーム一覧の取得
 *
 * Returns list of teams.
 */
export function getListOfTeams(
	config: BacklogConfig,
	params?: GetListOfTeamsParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Team[]>> {
	return requestApiGet({
		config,
		path: "teams",
		params,
		context,
	});
}

/**
 * Add Team チームの追加
 *
 * Adds new team. You can’t use this API at new plan space.
 *
 */
export function addTeam(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("not supported.");
}

/**
 * Get Team チーム情報の取得
 *
 * Returns information about team.
 */
export function getTeam(
	config: BacklogConfig,
	params: GetTeamParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Team>> {
	assert(params);
	return requestApiGet({
		config,
		path: "teams/:teamId",
		params,
		context,
	});
}

/**
 * Update Team チーム情報の更新
 *
 * Updates information about team. You can’t use this API at new plan space.
 *
 */
export function updateTeam(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("not supported.");
}

/**
 * Delete Team チームの削除
 *
 * Deletes team. You can’t use this API at new plan space.
 *
 */
export function deleteTeam(
	config: BacklogConfig,
	params: EmptyObject,
	context?: BacklogApiContext,
): Promise<BacklogResponse<EmptyObject>> {
	throw new BacklogError("not supported.");
}

/**
 * Get Team Icon チームアイコンの取得
 *
 * Downloads team icon.
 */
export function getTeamIcon(
	config: BacklogConfig,
	params: GetTeamIconParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<BacklogResponseFile>> {
	assert(params);
	return requestApiGetFile({
		config,
		path: "teams/:teamId/icon",
		params,
		context,
	});
}

/**
 * Get Project Team List プロジェクトチーム一覧の取得
 *
 * Returns list of project teams.
 */
export function getProjectTeamList(
	config: BacklogConfig,
	params: GetProjectTeamListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Team[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "projects/:projectIdOrKey/teams",
		params,
		context,
	});
}

/**
 * Add Project Team プロジェクトチームの追加
 *
 * Add team to project.
 */
export function addProjectTeam(
	config: BacklogConfig,
	params: AddProjectTeamParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Team>> {
	assert(params);
	return requestApiPost({
		config,
		path: "projects/:projectIdOrKey/teams",
		params,
		context,
	});
}

/**
 * Delete Project Team プロジェクトチームの削除
 *
 * Removes a team from the project.
 */
export function deleteProjectTeam(
	config: BacklogConfig,
	params: DeleteProjectTeamParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Team>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "projects/:projectIdOrKey/teams",
		params,
		context,
	});
}
